import { apiUrl } from '../apiBase';

const ADMIN_ROLE_KEY = 'admin_role';
export const ADMIN_UPLOAD_STATE_EVENT = 'admin-upload-state';
let activeUploadCount = 0;

function emitUploadState() {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return;
  window.dispatchEvent(
    new CustomEvent(ADMIN_UPLOAD_STATE_EVENT, {
      detail: { active: activeUploadCount > 0, count: activeUploadCount },
    })
  );
}

function beginUpload() {
  activeUploadCount += 1;
  emitUploadState();
}

function endUpload() {
  activeUploadCount = Math.max(0, activeUploadCount - 1);
  emitUploadState();
}

export function getToken() {
  return localStorage.getItem('admin_token');
}

/** `admin` (default) or `editor` — content-only users. */
export function getRole() {
  return localStorage.getItem(ADMIN_ROLE_KEY) || 'admin';
}

export function setToken(t, role) {
  if (t) {
    localStorage.setItem('admin_token', t);
    const r = role === 'editor' ? 'editor' : 'admin';
    localStorage.setItem(ADMIN_ROLE_KEY, r);
  } else {
    localStorage.removeItem('admin_token');
    localStorage.removeItem(ADMIN_ROLE_KEY);
  }
}

export async function fetchSite() {
  const r = await fetch(apiUrl('/api/site'));
  if (!r.ok) throw new Error('Failed to load site');
  return r.json();
}

export async function loginRequest(mobile, password) {
  const payload = {
    mobile: typeof mobile === 'string' ? mobile.trim() : '',
    password,
  };
  const r = await fetch(apiUrl('/api/auth/login'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) {
    throw new Error(data.error || 'Login failed');
  }
  return data;
}

export async function changePassword(currentPassword, newPassword) {
  const r = await fetch(apiUrl('/api/auth/password'), {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify({ currentPassword, newPassword }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || 'Could not update password');
  return data;
}

export async function fetchUsers() {
  const r = await fetch(apiUrl('/api/admin/users'), {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || 'Failed to load users');
  return data;
}

export async function createEditorUser(mobile, password) {
  const r = await fetch(apiUrl('/api/admin/users'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify({
      mobile: typeof mobile === 'string' ? mobile.trim() : '',
      password,
    }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || 'Create user failed');
  return data;
}

export async function deleteUser(phone) {
  const r = await fetch(apiUrl(`/api/admin/users/${encodeURIComponent(phone)}`), {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || 'Delete failed');
  return data;
}

export async function saveSite(site) {
  const r = await fetch(apiUrl('/api/site'), {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify(site),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || 'Save failed');
  }
  return r.json();
}

export async function patchProductsSection(section) {
  const r = await fetch(apiUrl('/api/admin/products/section'), {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify(section),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || 'Save failed');
  return data;
}

export async function createProduct(product) {
  const r = await fetch(apiUrl('/api/admin/products'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify(product),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || 'Create failed');
  return data;
}

export async function updateProduct(productId, product) {
  const r = await fetch(apiUrl(`/api/admin/products/${encodeURIComponent(productId)}`), {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify(product),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || 'Update failed');
  return data;
}

export async function deleteProduct(productId) {
  const r = await fetch(apiUrl(`/api/admin/products/${encodeURIComponent(productId)}`), {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || 'Delete failed');
  return data;
}

export async function fetchSeedVarietiesAdmin() {
  const r = await fetch(apiUrl('/api/admin/seed-varieties'), {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || 'Failed to load variety catalog');
  return data.seedVarieties;
}

export async function createSeedVarietyCategory(payload) {
  const r = await fetch(apiUrl('/api/admin/seed-varieties/categories'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify(payload),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || 'Create failed');
  return {
    seedVarieties: data.seedVarieties,
    createdCategoryId: data.createdCategoryId,
  };
}

export async function updateSeedVarietyCategory(categoryId, payload) {
  const r = await fetch(apiUrl(`/api/admin/seed-varieties/categories/${encodeURIComponent(categoryId)}`), {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify(payload),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || 'Update failed');
  return data.seedVarieties;
}

export async function deleteSeedVarietyCategory(categoryId) {
  const r = await fetch(apiUrl(`/api/admin/seed-varieties/categories/${encodeURIComponent(categoryId)}`), {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || 'Delete failed');
  return data.seedVarieties;
}

export async function createSeedVarietyProduct(payload) {
  const r = await fetch(apiUrl('/api/admin/seed-varieties/products'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify(payload),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || 'Create failed');
  return {
    seedVarieties: data.seedVarieties,
    createdProductId: data.createdProductId,
  };
}

export async function updateSeedVarietyProduct(productId, payload) {
  const r = await fetch(apiUrl(`/api/admin/seed-varieties/products/${encodeURIComponent(productId)}`), {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify(payload),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || 'Update failed');
  return data.seedVarieties;
}

export async function deleteSeedVarietyProduct(productId) {
  const r = await fetch(apiUrl(`/api/admin/seed-varieties/products/${encodeURIComponent(productId)}`), {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || 'Delete failed');
  return data.seedVarieties;
}

export async function createSeedVarietyVariant(productId, payload) {
  const r = await fetch(
    apiUrl(`/api/admin/seed-varieties/products/${encodeURIComponent(productId)}/variants`),
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify(payload),
    }
  );
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || 'Create failed');
  return data.seedVarieties;
}

export async function updateSeedVarietyVariant(productId, variantId, payload) {
  const r = await fetch(
    apiUrl(
      `/api/admin/seed-varieties/products/${encodeURIComponent(productId)}/variants/${encodeURIComponent(variantId)}`
    ),
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify(payload),
    }
  );
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || 'Update failed');
  return data.seedVarieties;
}

export async function deleteSeedVarietyVariant(productId, variantId) {
  const r = await fetch(
    apiUrl(
      `/api/admin/seed-varieties/products/${encodeURIComponent(productId)}/variants/${encodeURIComponent(variantId)}`
    ),
    {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${getToken()}` },
    }
  );
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || 'Delete failed');
  return data.seedVarieties;
}

export async function patchNewsSection(section) {
  const r = await fetch(apiUrl('/api/admin/news/section'), {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify(section),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || 'Save failed');
  return data;
}

export async function createNewsArticle(article) {
  const r = await fetch(apiUrl('/api/admin/news'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify(article),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || 'Create failed');
  return data;
}

export async function updateNewsArticle(articleId, article) {
  const r = await fetch(apiUrl(`/api/admin/news/${encodeURIComponent(articleId)}`), {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify(article),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || 'Update failed');
  return data;
}

export async function deleteNewsArticle(articleId) {
  const r = await fetch(apiUrl(`/api/admin/news/${encodeURIComponent(articleId)}`), {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || 'Delete failed');
  return data;
}

export async function patchGallerySection(section) {
  const r = await fetch(apiUrl('/api/admin/gallery/section'), {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify(section),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || 'Save failed');
  return data;
}

export async function createGalleryItem(item) {
  const r = await fetch(apiUrl('/api/admin/gallery/items'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify(item),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || 'Create failed');
  return data;
}

export async function updateGalleryItem(itemId, item) {
  const r = await fetch(apiUrl(`/api/admin/gallery/items/${encodeURIComponent(itemId)}`), {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify(item),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || 'Update failed');
  return data;
}

export async function deleteGalleryItem(itemId) {
  const r = await fetch(apiUrl(`/api/admin/gallery/items/${encodeURIComponent(itemId)}`), {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || 'Delete failed');
  return data;
}

/**
 * @param {string[] | {
 *   names: string[],
 *   sectionLabel?: string,
 *   sectionTitle?: string,
 *   centerImageSrc?: string,
 *   centerImageAlt?: string,
 *   features?: Array<{ step?: string, title?: string, text?: string, ctaLabel?: string, ctaHref?: string }>,
 * }} payload
 */
export async function patchBrandTicker(payload) {
  const body = Array.isArray(payload)
    ? payload
    : {
        names: Array.isArray(payload.names) ? payload.names : [],
        sectionLabel: payload.sectionLabel,
        sectionTitle: payload.sectionTitle,
        centerImageSrc: payload.centerImageSrc,
        centerImageAlt: payload.centerImageAlt,
        features: payload.features,
      };
  const r = await fetch(apiUrl('/api/admin/brand-ticker'), {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify(body),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || 'Save failed');
  return data;
}

export async function patchSustainableFarming(block) {
  const r = await fetch(apiUrl('/api/admin/sustainable-farming'), {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify(block && typeof block === 'object' ? block : {}),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || 'Save failed');
  return data;
}

export async function patchSeo(block) {
  const r = await fetch(apiUrl('/api/admin/seo'), {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify(block && typeof block === 'object' ? block : {}),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || 'Save failed');
  return data;
}

export async function patchAbout(about) {
  const r = await fetch(apiUrl('/api/admin/about'), {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify(about && typeof about === 'object' ? about : {}),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || 'Save failed');
  return data;
}

export async function patchHistory(history) {
  const r = await fetch(apiUrl('/api/admin/history'), {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify(history && typeof history === 'object' ? history : {}),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || 'Save failed');
  return data;
}

export async function patchFarmer(farmer) {
  const r = await fetch(apiUrl('/api/admin/farmer'), {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify(farmer && typeof farmer === 'object' ? farmer : {}),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || 'Save failed');
  return data;
}

export async function patchFounderDesk(block) {
  const r = await fetch(apiUrl('/api/admin/founder-desk'), {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify(block && typeof block === 'object' ? block : {}),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || 'Save failed');
  return data;
}

export async function patchHero(hero) {
  const r = await fetch(apiUrl('/api/admin/hero'), {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify(hero && typeof hero === 'object' ? hero : {}),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || 'Save failed');
  return data;
}

export async function patchHeader(header) {
  const r = await fetch(apiUrl('/api/admin/header'), {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify(header && typeof header === 'object' ? header : {}),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || 'Save failed');
  return data;
}

export async function patchImpactStats(rows) {
  const r = await fetch(apiUrl('/api/admin/impact-stats'), {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify(Array.isArray(rows) ? rows : []),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || 'Save failed');
  return data;
}

const DEFAULT_ADMIN_UPLOAD_MAX_FILE_BYTES = 5 * 1024 * 1024;

function adminUploadMaxFileBytes() {
  const raw = process.env.REACT_APP_ADMIN_UPLOAD_MAX_FILE_BYTES;
  if (raw === undefined || raw === '') return DEFAULT_ADMIN_UPLOAD_MAX_FILE_BYTES;
  const n = parseInt(String(raw), 10);
  return Number.isFinite(n) && n >= 1024 ? n : DEFAULT_ADMIN_UPLOAD_MAX_FILE_BYTES;
}

/** Kept in sync with backend `ADMIN_UPLOAD_MAX_FILE_BYTES` / multer `fileSize` (default 5 MB). */
export const ADMIN_UPLOAD_MAX_FILE_BYTES = adminUploadMaxFileBytes();

export function formatUploadSizeForMessage(bytes) {
  if (bytes >= 1024 * 1024) {
    const mb = bytes / (1024 * 1024);
    return Number.isInteger(mb) ? `${mb} MB` : `${mb.toFixed(1).replace(/\.0$/, '')} MB`;
  }
  if (bytes >= 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${bytes} bytes`;
}

export async function uploadFile(file) {
  if (!file || typeof file.size !== 'number') {
    throw new Error('No file selected');
  }
  if (file.size <= 0) {
    throw new Error('File is empty');
  }
  if (file.size > ADMIN_UPLOAD_MAX_FILE_BYTES) {
    throw new Error(
      `File is too large (${formatUploadSizeForMessage(file.size)}). Maximum size is ${formatUploadSizeForMessage(ADMIN_UPLOAD_MAX_FILE_BYTES)}.`
    );
  }
  const token = getToken();
  if (!token) {
    throw new Error('Not signed in — refresh the page and log in again');
  }
  beginUpload();
  const fd = new FormData();
  const name = typeof file.name === 'string' && file.name.trim() ? file.name.trim() : 'image.jpg';
  fd.append('file', file, name);
  try {
    const r = await fetch(apiUrl('/api/upload'), {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
    });
    const text = await r.text();
    let data = {};
    try {
      data = text ? JSON.parse(text) : {};
    } catch {
      data = {};
    }
    if (!r.ok) {
      throw new Error(data.error || text?.slice(0, 240) || `Upload failed (${r.status})`);
    }
    if (!data.url || typeof data.url !== 'string') {
      throw new Error('Server did not return an image URL');
    }
    return data;
  } finally {
    endUpload();
  }
}
